import { useEffect, useLayoutEffect, useMemo, useRef, useState, type RefObject } from "react";
import { useMediaQuery } from "./useMediaQuery";

/**
 * How long a departing row is given to dissolve before the list closes over
 * it, and how long a surviving row takes to glide from its old slot to its
 * new one. Both are short on purpose: this is feedback on a filter click, not
 * a transition the reader waits through.
 */
const EXIT_MS = 150;
const MOVE_MS = 220;
const EASE = "cubic-bezier(0.2, 0, 0, 1)";

/** Nothing is leaving — a shared instance, so `exitingIds` keeps its identity. */
const NO_EXITS: ReadonlySet<string> = new Set<string>();

interface Identified {
  id: string;
}

interface AnimatedList<T> {
  /**
   * What to render this frame. Normally the incoming list; while rows are
   * dissolving it is still the OUTGOING one, because a row cannot animate
   * away after it has been unmounted.
   */
  items: T[];
  /** The ids in `items` that are on screen only so they can leave. */
  exitingIds: ReadonlySet<string>;
}

/**
 * A list whose membership changes without anything teleporting.
 *
 * Filtering and sorting a table are the two operations that rewrite where
 * every row sits, and doing it in one commit reads as a jump cut: the rows
 * that no longer match disappear and the rest snap up the page, with nothing
 * on screen to connect the two states. This runs the change as two beats.
 *
 *  1. **Leave.** The hook keeps rendering the old list for {@link EXIT_MS},
 *     with the departing ids reported in `exitingIds` so the caller can mark
 *     them (`.row-exit` fades them out in place). Layout is untouched, so
 *     nothing moves yet.
 *  2. **Close.** The new list commits, and a FLIP pass plays every element
 *     tagged `data-flip-id` inside `containerRef` from where it *was* to
 *     where it now *is* — one `translateY` per element, animated to identity.
 *     Because the tags go on group headers and block chrome as well as on
 *     item rows, a block that gained or lost rows appears to resize rather
 *     than to redraw.
 *
 * A reorder with no departures skips straight to the second beat.
 *
 * FLIP is safe on `<tr>` here specifically because nothing inside these blocks
 * is `position: sticky` any more — a transformed ancestor would otherwise take
 * over as the containing block and strand the sticky cell.
 *
 * Membership is compared by an id signature rather than by array identity:
 * realtime edits hand down a fresh array many times a minute, and re-running
 * the whole dance for a changed title would animate the table at random.
 *
 * `prefers-reduced-motion` commits immediately, with no exit beat and no FLIP.
 */
export function useAnimatedList<T extends Identified>(
  incoming: T[],
  containerRef: RefObject<HTMLElement | null>,
): AnimatedList<T> {
  const reduceMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const [items, setItems] = useState<T[]>(incoming);
  const [exitingIds, setExitingIds] = useState<ReadonlySet<string>>(NO_EXITS);

  // The rendered list, mirrored, so the effect below can read it without
  // taking it as a dependency and re-running on its own output.
  const itemsRef = useRef(items);
  itemsRef.current = items;

  const signature = useMemo(() => incoming.map((item) => item.id).join("|"), [incoming]);
  const signatureRef = useRef(signature);

  /**
   * Where every tagged element sat before the commit, in the scroller's own
   * coordinates — `getBoundingClientRect().top` alone would be wrong the
   * moment the reader scrolls between the two beats.
   */
  const beforeRef = useRef<Map<string, number> | null>(null);

  const measure = (): Map<string, number> => {
    const map = new Map<string, number>();
    const root = containerRef.current;
    if (!root) return map;
    const scroll = root.scrollTop;
    for (const node of root.querySelectorAll<HTMLElement>("[data-flip-id]")) {
      const id = node.dataset.flipId;
      if (id) map.set(id, node.getBoundingClientRect().top + scroll);
    }
    return map;
  };

  useEffect(() => {
    if (signature === signatureRef.current) {
      // Same rows in the same order — adopt the new objects (a title edit, a
      // status change) without animating anything.
      if (incoming !== itemsRef.current) setItems(incoming);
      return;
    }
    signatureRef.current = signature;

    if (reduceMotion) {
      beforeRef.current = null;
      setExitingIds(NO_EXITS);
      setItems(incoming);
      return;
    }

    // Taken now, while the DOM still shows the outgoing list.
    beforeRef.current = measure();

    const staying = new Set(incoming.map((item) => item.id));
    const leaving = new Set(itemsRef.current.filter((item) => !staying.has(item.id)).map((item) => item.id));

    if (leaving.size === 0) {
      setExitingIds(NO_EXITS);
      setItems(incoming);
      return;
    }

    setExitingIds(leaving);
    const timer = setTimeout(() => {
      setExitingIds(NO_EXITS);
      setItems(incoming);
    }, EXIT_MS);
    return () => clearTimeout(timer);
    // `incoming` rides along with the signature it was measured from; adding
    // it as a dependency of its own would re-arm the exit on every realtime
    // patch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, reduceMotion]);

  // The second beat. Layout has already been recalculated for the new list;
  // put every survivor back where it was and let it drive itself home.
  useLayoutEffect(() => {
    const before = beforeRef.current;
    beforeRef.current = null;
    const root = containerRef.current;
    if (!before || !root) return;

    // Every read first, then every write: interleaving `getBoundingClientRect`
    // with `animate` on a 60-row table forces a layout per row.
    const scroll = root.scrollTop;
    const moves: Array<[HTMLElement, number]> = [];
    for (const node of root.querySelectorAll<HTMLElement>("[data-flip-id]")) {
      const id = node.dataset.flipId;
      if (!id) continue;
      const was = before.get(id);
      // A row that wasn't there before arrives on its own cascade; it has no
      // old position to travel from.
      if (was === undefined) continue;
      const delta = was - (node.getBoundingClientRect().top + scroll);
      if (Math.abs(delta) >= 1) moves.push([node, delta]);
    }
    for (const [node, delta] of moves) {
      node.animate([{ transform: `translateY(${delta}px)` }, { transform: "translateY(0)" }], {
        duration: MOVE_MS,
        easing: EASE,
      });
    }
  }, [items, containerRef]);

  return { items, exitingIds };
}
